import React from 'react'

import { Icon, List, Message, Popup } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { GETTenderEngineShopInfo, ITenderEngineInformation } from '@cbgms/api/modules/tender-engine'
import { getTenderEngineShopInfo } from '../state/actions'
import { connect } from 'react-redux'
import { useAddressFormatter } from '@cbgms/components/general/Address'
import { Loader } from '@carsys/ui'

interface OuterProps {
  conceptUUID: string
  shopReferenceUUID: string
  disabled: boolean
}

interface MapDispatchToProps {
  getTenderEngineShopInfo: typeof getTenderEngineShopInfo
}

interface TenderShopInfoPopupProps extends OuterProps, MapDispatchToProps {}

const mapDispatchToProps: MapDispatchToProps = { getTenderEngineShopInfo }

const enhance = connect(null, mapDispatchToProps)

export const TenderShopInfoPopup: React.FC<TenderShopInfoPopupProps> = ({
  getTenderEngineShopInfo,
  shopReferenceUUID,
  conceptUUID,
  disabled
}) => {
  const [tenderEngineShop, setTenderEngineShop] = React.useState<ITenderEngineInformation | undefined>(undefined)
  const [error, setError] = React.useState<string | undefined>(undefined)

  const { t } = useTranslation()
  const { formatAddressStreet, formatAddressCity } = useAddressFormatter()

  const onOpen = async () => {
    try {
      const response = (await getTenderEngineShopInfo(shopReferenceUUID, conceptUUID)) as any as GETTenderEngineShopInfo

      setTenderEngineShop(response.Data)
      setError(undefined)
    } catch {
      setError(t('TenderEngine.ErrorLoadingShopInfo'))
    }
  }

  const onClose = () => {
    setTenderEngineShop(undefined)
    setError(undefined)
  }

  return (
    <Popup
      basic
      trigger={<Icon data-testid='shop-concepts-line-info' disabled={disabled} fitted link name='info circle' />}
      on='click'
      onClose={onClose}
      onOpen={onOpen}
      wide='very'
    >
      {tenderEngineShop && !error && (
        <>
          <List>
            <List.Item>
              <List.Content>
                <List.Header>{t('TenderEngine.UUID')}</List.Header>
                <List.Description>{tenderEngineShop.TenderEngineWorkshopID}</List.Description>
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Content>
                <List.Header>{t('TenderEngine.ShopName')}</List.Header>
                <List.Description>{tenderEngineShop.Profile.WorkshopName}</List.Description>
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Content>
                <List.Header>{t('TenderEngine.Address')}</List.Header>
                <List.Description>{formatAddressStreet(tenderEngineShop.Profile)}</List.Description>
                <List.Description>{formatAddressCity(tenderEngineShop.Profile)}</List.Description>
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Content>
                <List.Header>{t('TenderEngine.PhoneNumber')}</List.Header>
                <List.Description>{tenderEngineShop.Profile.PhoneNumber}</List.Description>
              </List.Content>
            </List.Item>
            <List.Item>
              <List.Content>
                <List.Header>{t('TenderEngine.Email')}</List.Header>
                <List.Description>{tenderEngineShop.Profile.EmailAddress}</List.Description>
              </List.Content>
            </List.Item>
          </List>
          {tenderEngineShop.IsAppointmentKeyMissing && (
            <Message info>
              <Icon name='info circle' />
              {t('TenderEngine.AppointmentKeyMissing')}
            </Message>
          )}
        </>
      )}
      {!tenderEngineShop && !error && (
        <div className='m2'>
          <Loader loading />
        </div>
      )}
      {error && (
        <Message negative>
          <Icon name='times circle' />
          {error}
        </Message>
      )}
    </Popup>
  )
}

export default enhance(TenderShopInfoPopup)
